import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { readOnlyMessage } from './readOnlyNotice';
import { KATALOG_VERSION, ergaenzeHistorie } from '../components/bauordnung/bauordnungLogic';
import { gezogeneKapitel, type Dokumentart } from '../components/bauordnung/kapitel';
import { alsEigenesKapitel } from '../components/bauordnung/bauordnungLogic';
import {
    AUTOMATIK_HANDBUCH_NAME, AUTOMATIK_KATALOG_NAME, NEUE_FASSUNG_TEXT,
    abgewaehltMarker, automatikFehlt, doppelteRegeln, doppelteRollen, ersetzePlatzhalter, kiFehlt,
    platzhalterAusVerweis, platzhalterVerweis,
    type AutomatikPaket, type NeueRegel,
} from '../components/bauordnung/bausteine/automatik';
import { platzhalterDefinition } from '../components/bauordnung/bausteine/platzhalter';
import {
    GEBUCHTE_MENGE_SPALTE, GEBUCHTE_POSITION_SPALTE, GEBUCHT_AM_SPALTE,
} from '../components/bauordnung/bauordnungBooking';
import {
    HANDBUCH_FELD_LABEL, alsRegelVorlage, type HandbuchUebernahme,
} from '../components/bauordnung/uebernahmeHandbuch';
import type {
    Abschnitt,
    Bauordnung,
    BauordnungBundle,
    BauordnungStatus,
    HistorienEintrag,
    KapitelZeile,
    Luecke,
    Regel,
    Rolle,
} from '../components/bauordnung/types';
import type { ProjektMerkmale } from '../components/planungsphase/types';

/**
 * Der einzige Supabase-Zugriff des Moduls 06 (Tabellen `bauordnungen`,
 * `bauordnung_*` — supabase/bauordnung.sql, MIGRATIONS.md Nr. 43).
 *
 * Die REGELN liegen daneben in `src/components/bauordnung/` und sind ohne
 * Datenbank pruefbar; hier steht nur, was ohne Supabase nicht zu haben ist.
 *
 * ═══ VIER HAUSREGELN — wortgleich zu asplanService.ts ═════════════════════
 *
 * (a) LESEN IST FAIL-OPEN, SCHREIBEN NICHT. Fehlt die Tabelle (Migration
 *     Nr. 43 noch nicht gefahren), liefert das Lesen eine leere Lage mit
 *     `schemaFehlt` statt zu werfen — die Ansicht sagt dann, was zu tun ist,
 *     statt weiss zu bleiben. Jeder schreibende Aufruf meldet den Fehler
 *     dagegen sichtbar.
 *
 * (b) ZWEITE REIHE DES SCHREIBSCHUTZES. Jeder schreibende Aufruf verlangt
 *     einen `SchreibKontext` und prueft ihn selbst. Weil der Kontext ein
 *     Pflichtparameter ist, kann ein neuer Schreibweg die Pruefung nicht
 *     vergessen — der Compiler laesst ihn sonst nicht uebersetzen.
 *
 * (c) KEIN LESEN-DANN-SCHREIBEN. Wo eine Bedingung gilt (nur Entwuerfe
 *     aendern, nur einmal freigeben), steht sie als `where`-Bedingung IM
 *     UPDATE. Zwischen Lesen und Schreiben passt eine zweite Sitzung.
 *
 * (d) KEIN AUTOMATISCHES ANLEGEN. `fetchBauordnungBundle` legt nichts an.
 *     Ein Projekt ohne Baustellenordnung bleibt ohne, bis jemand
 *     `ensureBauordnung` ausloest — beim blossen Oeffnen wird nichts
 *     geschrieben.
 */

const T_KOPF = 'bauordnungen';
const T_ABSCHNITT = 'bauordnung_abschnitte';
const T_REGEL = 'bauordnung_regeln';
const T_ROLLE = 'bauordnung_rollen';
const T_LUECKE = 'bauordnung_luecken';
const T_KAPITEL = 'bauordnung_kapitel';

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeTabelle = (e: unknown): boolean =>
    errorCode(e) === 'PGRST205' || errorCode(e) === '42P01';

const LEER: BauordnungBundle = {
    ordnung: null, abschnitte: [], regeln: [], rollen: [], luecken: [], eigeneKapitel: [],
};

// ═══════════════════════════════════════════════════════════════════════════
// SCHREIBSCHUTZ — die zweite Reihe
// ═══════════════════════════════════════════════════════════════════════════

export interface SchreibKontext {
    /** Nur-Ansicht: aus „Offene Positionen" geoeffnet oder fremde Projektsperre. */
    nurAnsicht: boolean;
    /** Status, wie ihn der Container beim Klick kannte. */
    status?: BauordnungStatus;
    /** Klarname des Handelnden — steht in Historie und Bestaetigungen. */
    handelnder?: string;
}

const GESPERRT_TEXT = 'Die Baustellenordnung ist freigegeben und eingefroren. '
    + 'Für Änderungen bitte eine neue Fassung eröffnen. Die freigegebene bleibt erhalten.';

const pruefeSchreibrecht = (ctx: SchreibKontext, was: string): void => {
    if (!ctx || ctx.nurAnsicht) throw new Error(readOnlyMessage(was));
};

const pruefeAenderbar = (ctx: SchreibKontext, was: string): void => {
    pruefeSchreibrecht(ctx, was);
    if (ctx.status === 'freigegeben') throw new Error(GESPERRT_TEXT);
};

const heute = (): string => {
    const d = new Date();
    // Kalendertag der ORTSZEIT. `toISOString()` liefert das UTC-Datum und
    // datiert einen Vorgang kurz nach Mitternacht auf den Vortag zurueck.
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// ABBILDUNG
// ═══════════════════════════════════════════════════════════════════════════

const liste = (wert: unknown): string[] => (Array.isArray(wert) ? wert.map(String) : []);

/** `numeric` kommt aus PostgREST als Zeichenkette; leer bleibt `null`. */
const zahlOderNull = (wert: unknown): number | null => {
    if (wert === null || wert === undefined || wert === '') return null;
    const n = Number(wert);
    return Number.isFinite(n) ? n : null;
};

const alsKopf = (r: Record<string, unknown>): Bauordnung => ({
    id: String(r.id),
    project_id: String(r.project_id),
    geltung_ort: String(r.geltung_ort ?? ''),
    geltung_von: (r.geltung_von as string) ?? null,
    geltung_bis: (r.geltung_bis as string) ?? null,
    adressaten: liste(r.adressaten),
    org_modell: (r.org_modell as Bauordnung['org_modell']) ?? null,
    erlassender_rolle: (r.erlassender_rolle as Bauordnung['erlassender_rolle']) ?? null,
    erlassender_firma: String(r.erlassender_firma ?? ''),
    erlassender_name: String(r.erlassender_name ?? ''),
    erlassender_funktion: String(r.erlassender_funktion ?? ''),
    verfasser_zeile: String(r.verfasser_zeile ?? ''),
    erzeugt_ordnung: r.erzeugt_ordnung !== false,
    erzeugt_hse: r.erzeugt_hse === true,
    hse_anlass: (r.hse_anlass as Bauordnung['hse_anlass']) ?? null,
    hse_zusatzkapitel: liste(r.hse_zusatzkapitel),
    katalog_version: String(r.katalog_version ?? ''),
    stand: (r.stand as string) ?? null,
    status: (r.status as BauordnungStatus) ?? 'entwurf',
    version_label: (r.version_label as string) ?? null,
    freigegeben_am: (r.freigegeben_am as string) ?? null,
    freigegeben_von_name: String(r.freigegeben_von_name ?? ''),
    historie: Array.isArray(r.historie) ? (r.historie as Bauordnung['historie']) : [],
    eingefroren_kapitel: (r.eingefroren_kapitel as Bauordnung['eingefroren_kapitel']) ?? null,
    eingefroren_merkmale: (r.eingefroren_merkmale as Bauordnung['eingefroren_merkmale']) ?? null,
    dokument_id_ordnung: (r.dokument_id_ordnung as string) ?? null,
    dokument_id_hse: (r.dokument_id_hse as string) ?? null,
    uebernommen_handbuch_am: (r.uebernommen_handbuch_am as string) ?? null,
    gebucht_ordnung_am: (r.gebucht_ordnung_am as string) ?? null,
    gebuchte_ordnung_position_id: (r.gebuchte_ordnung_position_id as string) ?? null,
    gebuchte_ordnung_menge: zahlOderNull(r.gebuchte_ordnung_menge),
    gebucht_hse_am: (r.gebucht_hse_am as string) ?? null,
    gebuchte_hse_position_id: (r.gebuchte_hse_position_id as string) ?? null,
    gebuchte_hse_menge: zahlOderNull(r.gebuchte_hse_menge),
    updated_at: (r.updated_at as string) ?? undefined,
});

const alsAbschnitt = (r: Record<string, unknown>): Abschnitt => ({
    id: String(r.id),
    project_id: String(r.project_id),
    ordnung_id: String(r.ordnung_id),
    kapitel: String(r.kapitel),
    gezogen: r.gezogen === true,
    zustand: (r.zustand as Abschnitt['zustand']) ?? 'offen',
    einleitung: String(r.einleitung ?? ''),
    bemerkung: String(r.bemerkung ?? ''),
    sortierung: Number(r.sortierung ?? 0),
});

const alsRegel = (r: Record<string, unknown>): Regel => ({
    id: String(r.id),
    project_id: String(r.project_id),
    ordnung_id: String(r.ordnung_id),
    kapitel: String(r.kapitel),
    baustein_id: (r.baustein_id as string) ?? null,
    katalog_version: String(r.katalog_version ?? ''),
    wortlaut: String(r.wortlaut ?? ''),
    art: (r.art as Regel['art']) ?? 'regel',
    zielgruppe: liste(r.zielgruppe),
    rhythmus: String(r.rhythmus ?? ''),
    verantwortlich: String(r.verantwortlich ?? ''),
    sige_bezug: String(r.sige_bezug ?? ''),
    quelle: (r.quelle as Regel['quelle']) ?? 'frei',
    bestaetigt_am: (r.bestaetigt_am as string) ?? null,
    bestaetigt_von_name: String(r.bestaetigt_von_name ?? ''),
    sortierung: Number(r.sortierung ?? 0),
});

const alsRolle = (r: Record<string, unknown>): Rolle => ({
    id: String(r.id),
    project_id: String(r.project_id),
    ordnung_id: String(r.ordnung_id),
    rolle: String(r.rolle ?? ''),
    funktion: String(r.funktion ?? ''),
    firma: String(r.firma ?? ''),
    name: String(r.name ?? ''),
    erreichbarkeit: String(r.erreichbarkeit ?? ''),
    weisungsbefugnis: (r.weisungsbefugnis as Rolle['weisungsbefugnis']) ?? 'keine',
    befugnis_umfang: String(r.befugnis_umfang ?? ''),
    bestellt_am: (r.bestellt_am as string) ?? null,
    sortierung: Number(r.sortierung ?? 0),
});

const alsLuecke = (r: Record<string, unknown>): Luecke => ({
    id: String(r.id),
    project_id: String(r.project_id),
    ordnung_id: String(r.ordnung_id),
    nr: Number(r.nr ?? 0),
    kapitel: String(r.kapitel ?? ''),
    regel_id: (r.regel_id as string) ?? null,
    punkt: String(r.punkt ?? ''),
    verweis: String(r.verweis ?? ''),
    verantwortlich: String(r.verantwortlich ?? ''),
    frist: (r.frist as string) ?? null,
    erledigt_am: (r.erledigt_am as string) ?? null,
    pflicht: r.pflicht !== false,
    herkunft: (r.herkunft as Luecke['herkunft']) ?? 'manuell',
    sortierung: Number(r.sortierung ?? 0),
});

const alsKapitelZeile = (r: Record<string, unknown>): KapitelZeile => ({
    id: String(r.id),
    company_id: (r.company_id as string) ?? null,
    company_name: String(r.company_name ?? ''),
    kapitel_id: String(r.kapitel_id),
    titel: String(r.titel ?? ''),
    dokument: (r.dokument as Dokumentart) ?? 'hse',
    teil: String(r.teil ?? 'basic'),
    nach: (r.nach as string) ?? null,
    pflicht: r.pflicht === true,
    waehlbar: r.waehlbar !== false,
    quelle: liste(r.quelle),
    wenn: liste(r.wenn),
    nicht_wenn: liste(r.nicht_wenn),
    angelegt_von_name: String(r.angelegt_von_name ?? ''),
});

// ═══════════════════════════════════════════════════════════════════════════
// LESEN
// ═══════════════════════════════════════════════════════════════════════════

/** Firma des angemeldeten Profils — Anker der firmenweiten Kapitel. */
export const meineFirma = async (): Promise<{ company_id: string | null; company_name: string }> => {
    const { data: auth } = await supabase.auth.getUser();
    const id = auth?.user?.id;
    if (!id) return { company_id: null, company_name: '' };
    const { data } = await supabase.from('profiles')
        .select('company_id, company_name').eq('id', id).maybeSingle();
    return {
        company_id: (data?.company_id as string) ?? null,
        company_name: String(data?.company_name ?? ''),
    };
};

export const fetchBauordnungBundle = async (projectId: string): Promise<BauordnungBundle> => {
    if (!projectId) return { ...LEER };
    try {
        const { data: kopfRoh, error } = await supabase
            .from(T_KOPF).select('*').eq('project_id', projectId).maybeSingle();
        if (error && !istFehlendeTabelle(error)) throw error;
        if (error) return { ...LEER, schemaFehlt: true };

        const eigene = await fetchEigeneKapitel();
        if (!kopfRoh) return { ...LEER, eigeneKapitel: eigene };

        const ordnung = alsKopf(kopfRoh as Record<string, unknown>);
        const kind = async <T>(tabelle: string, ab: (r: Record<string, unknown>) => T): Promise<T[]> => {
            const { data, error: e } = await supabase
                .from(tabelle).select('*').eq('ordnung_id', ordnung.id).order('sortierung');
            if (e) {
                if (istFehlendeTabelle(e)) return [];
                throw e;
            }
            return (data || []).map(r => ab(r as Record<string, unknown>));
        };

        const [abschnitte, regeln, rollen, luecken] = await Promise.all([
            kind(T_ABSCHNITT, alsAbschnitt),
            kind(T_REGEL, alsRegel),
            kind(T_ROLLE, alsRolle),
            kind(T_LUECKE, alsLuecke),
        ]);

        return { ordnung, abschnitte, regeln, rollen, luecken, eigeneKapitel: eigene };
    } catch (e) {
        if (istFehlendeTabelle(e)) return { ...LEER, schemaFehlt: true };
        throw e;
    }
};

/**
 * Die firmenweiten Kapitel.
 *
 * Ohne `eq` auf die Firma: das erledigt die RLS-Policy, und sie tut es
 * verlaesslicher als ein Filter im Client, den man vergessen kann.
 */
export const fetchEigeneKapitel = async (): Promise<KapitelZeile[]> => {
    const { data, error } = await supabase.from(T_KAPITEL).select('*').order('titel');
    if (error) {
        if (istFehlendeTabelle(error)) return [];
        throw error;
    }
    return (data || []).map(r => alsKapitelZeile(r as Record<string, unknown>));
};

// ═══════════════════════════════════════════════════════════════════════════
// KOPFSATZ
// ═══════════════════════════════════════════════════════════════════════════

/** Legt den Kopfsatz an — NUR auf ausdruecklichen Klick, nie beim Oeffnen. */
export const ensureBauordnung = async (
    projectId: string,
    ctx: SchreibKontext,
    vorbelegung?: { geltung_ort?: string; verfasser_zeile?: string },
): Promise<Bauordnung> => {
    pruefeSchreibrecht(ctx, 'Eine Baustellenordnung wurde nicht angelegt.');

    const { data: schon } = await supabase
        .from(T_KOPF).select('*').eq('project_id', projectId).maybeSingle();
    if (schon) return alsKopf(schon as Record<string, unknown>);

    const { data, error } = await supabase.from(T_KOPF).insert({
        project_id: projectId,
        // Die Katalogfassung MUSS der Client mitschreiben — die Spalte hat
        // keinen fachlich gueltigen Vorgabewert, und die Freigabe meldet eine
        // leere Fassung als Hindernis.
        katalog_version: KATALOG_VERSION,
        stand: heute(),
        // Vorbelegung ist ein VORSCHLAG und wird trotzdem geschrieben, weil
        // der Anwender sie im Formular sofort sieht und aendern kann. Was
        // NICHT vorbelegt wird, ist alles, was eine Aussage ueber das Projekt
        // trifft — Erlassender, Geltungsdauer, Adressaten bleiben leer.
        geltung_ort: vorbelegung?.geltung_ort ?? '',
        verfasser_zeile: vorbelegung?.verfasser_zeile ?? '',
        historie: ergaenzeHistorie([], ctx.handelnder || '', 'Baustellenordnung angelegt', new Date().toISOString()),
    }).select('*').single();
    if (error) throw error;
    return alsKopf(data as Record<string, unknown>);
};

export const updateOrdnung = async (
    id: string,
    patch: Partial<Bauordnung>,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Änderung wurde nicht gespeichert.');
    const { error } = await supabase.from(T_KOPF)
        .update(patch)
        .eq('id', id)
        // Die Bedingung steht IM UPDATE, nicht davor: zwischen Lesen und
        // Schreiben passt eine zweite Sitzung, die freigibt.
        .eq('status', 'entwurf');
    if (error) throw error;
};

const HANDBUCH_SCHON_UEBERNOMMEN = 'Aus dem Handbuch wurde bereits übernommen. Die Übernahme läuft genau einmal, '
    + 'seitdem werden Handbuch und Baustellenordnung getrennt geführt.';

/**
 * Die EINMALIGE Uebernahme aus dem SiGe-Handbuch (Spezifikation D, E2).
 *
 * Was uebernommen wird, hat `uebernahmeAusHandbuch` (rein) vorher bestimmt;
 * hier wird nur geschrieben, in dieser Reihenfolge:
 *
 *  1. DER RIEGEL ZUERST: `uebernommen_handbuch_am` wird am Kopfsatz gesetzt —
 *     mit `is('uebernommen_handbuch_am', null)` und `eq('status','entwurf')`
 *     IM UPDATE. Trifft das keine Zeile, hat eine zweite Sitzung schon
 *     uebernommen oder freigegeben; dann wird NICHTS eingefuegt und das
 *     gemeldet. Ein vorgelagertes Lesen koennte das nicht leisten.
 *  2. Erst danach die Regeln (`quelle: 'handbuch'`, unbestaetigt). Das
 *     Modell geht im selben Update wie der Stempel mit — nur wenn der
 *     Entwurf eines mitbringt (also der Kopfsatz keines hatte).
 *
 * Scheitert Schritt 2, steht der Stempel schon. Das wird ausdruecklich
 * gemeldet und nicht zurueckgedreht: ein stilles Zuruecknehmen liesse beim
 * naechsten Klick dieselben Zeilen ein zweites Mal entstehen, wenn ein Teil
 * doch angekommen war.
 *
 * `opt.bestaetigt` (nur der Automatik-Lauf, Plan E7): die Zeilen kommen gleich
 * BESTAETIGT an — sie standen schon in einem freigegebenen Handbuch. Der Knopf
 * im Grundlagen-Reiter laesst das weg und behaelt die Vorschlagsform.
 */
export const uebernehmeAusHandbuch = async (
    ordnung: Bauordnung,
    uebernahme: HandbuchUebernahme,
    ctx: SchreibKontext,
    opt: { bestaetigt?: boolean } = {},
): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Übernahme aus dem Handbuch wurde nicht gespeichert.');
    if (ordnung.uebernommen_handbuch_am) throw new Error(HANDBUCH_SCHON_UEBERNOMMEN);

    const jetzt = new Date().toISOString();
    const felder = uebernahme.jeFeld.map(f => `${HANDBUCH_FELD_LABEL[f.feld]} (${f.anzahl})`);
    if (uebernahme.org_modell) felder.push(`Organisationsmodell ${uebernahme.org_modell}`);
    const was = `Aus dem Handbuch übernommen: ${felder.length ? felder.join(', ') : 'keine Inhalte'}`;

    // Die Historie NICHT in diesem Patch: der mitgegebene Kopfsatz kann veraltet
    // sein, und ein ganzes `historie`-Feld aus seinem Stand ueberschriebe
    // Marker, die eine zweite Sitzung inzwischen geschrieben hat (Abwahl, KI).
    // Der Eintrag kommt gleich danach ueber `ergaenzeKopfHistorie` — frisch
    // gelesen, mit Waechter (Opus-Review 02.11.2026).
    const patch: Record<string, unknown> = { uebernommen_handbuch_am: jetzt };
    if (uebernahme.org_modell) patch.org_modell = uebernahme.org_modell;

    const { data, error } = await supabase.from(T_KOPF).update(patch)
        .eq('id', ordnung.id)
        .eq('status', 'entwurf')
        .is('uebernommen_handbuch_am', null)
        .select('id');
    if (error) throw error;
    if (!data?.length) throw new Error(HANDBUCH_SCHON_UEBERNOMMEN);
    await ergaenzeKopfHistorie(ordnung, [was], ctx);

    if (!uebernahme.regeln.length) return;
    const bestaetigung = opt.bestaetigt
        ? { bestaetigt_am: jetzt, bestaetigt_von_name: AUTOMATIK_HANDBUCH_NAME }
        : {};
    const { error: regelFehler } = await supabase.from(T_REGEL).insert(
        uebernahme.regeln.map(e => ({
            project_id: ordnung.project_id,
            ordnung_id: ordnung.id,
            ...alsRegelVorlage(e),
            baustein_id: null,
            // Keine Katalogfassung: der Wortlaut stammt aus dem Handbuch, nicht
            // aus einem Baustein.
            katalog_version: '',
            verantwortlich: '',
            rhythmus: '',
            ...bestaetigung,
        })),
    );
    if (regelFehler) {
        throw new Error('Die Übernahme ist am Kopfsatz vermerkt, aber die Regelungen konnten nicht geschrieben '
            + `werden: ${describeError(regelFehler)} Bitte die Kapitel prüfen und fehlende Zeilen von Hand ergänzen.`);
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHNITTE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Legt die Kapitelzeile an oder aendert sie.
 *
 * Erst hier entsteht eine Zeile — beim Oeffnen des Moduls nicht. Eine
 * FEHLENDE Zeile heisst „noch nie angesehen", eine mit `gezogen: false`
 * heisst „geprueft und ausgeschlossen". Beides ist verschieden, und nur
 * dieser Aufruf macht aus dem einen das andere.
 */
export const setzeAbschnitt = async (
    ordnung: Bauordnung,
    kapitel: string,
    patch: Partial<Omit<Abschnitt, 'id' | 'project_id' | 'ordnung_id' | 'kapitel'>>,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeAenderbar(ctx, 'Das Kapitel wurde nicht gespeichert.');
    const { error } = await supabase.from(T_ABSCHNITT).upsert({
        project_id: ordnung.project_id,
        ordnung_id: ordnung.id,
        kapitel,
        gezogen: true,
        ...patch,
    }, { onConflict: 'ordnung_id,kapitel' });
    if (error) throw error;
};

// ═══════════════════════════════════════════════════════════════════════════
// REGELN — die Texte
// ═══════════════════════════════════════════════════════════════════════════

export const addRegel = async (
    ordnung: Bauordnung,
    kapitel: string,
    vorlage: Partial<Regel>,
    ctx: SchreibKontext,
): Promise<Regel> => {
    pruefeAenderbar(ctx, 'Die Regelung wurde nicht angelegt.');
    const { data, error } = await supabase.from(T_REGEL).insert({
        project_id: ordnung.project_id,
        ordnung_id: ordnung.id,
        kapitel,
        wortlaut: vorlage.wortlaut ?? '',
        baustein_id: vorlage.baustein_id ?? null,
        // Die Katalogfassung wird BEI DER UEBERNAHME geschrieben, nicht erst
        // bei der Freigabe: eine freigegebene Fassung muss auch in Jahren
        // noch erklaeren, was sie geregelt hat.
        katalog_version: vorlage.baustein_id ? KATALOG_VERSION : '',
        art: vorlage.art ?? 'regel',
        quelle: vorlage.quelle ?? 'frei',
        verantwortlich: vorlage.verantwortlich ?? '',
        rhythmus: vorlage.rhythmus ?? '',
        sortierung: vorlage.sortierung ?? 0,
    }).select('*').single();
    if (error) throw error;
    return alsRegel(data as Record<string, unknown>);
};

export const updateRegel = async (
    id: string,
    patch: Partial<Regel>,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Regelung wurde nicht gespeichert.');
    const { error } = await supabase.from(T_REGEL).update(patch).eq('id', id);
    if (error) throw error;
};

/** Bestaetigen heisst uebernehmen — erst danach geht die Regel ins Dokument. */
export const bestaetigeRegel = async (
    id: string,
    bestaetigt: boolean,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Bestätigung wurde nicht gespeichert.');
    const { error } = await supabase.from(T_REGEL).update({
        bestaetigt_am: bestaetigt ? new Date().toISOString() : null,
        bestaetigt_von_name: bestaetigt ? (ctx.handelnder || '') : '',
    }).eq('id', id);
    if (error) throw error;
};

export const deleteRegel = async (id: string, ctx: SchreibKontext): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Regelung wurde nicht gelöscht.');
    const { error } = await supabase.from(T_REGEL).delete().eq('id', id);
    if (error) throw error;
};

// ═══════════════════════════════════════════════════════════════════════════
// ROLLEN
// ═══════════════════════════════════════════════════════════════════════════

export const addRolle = async (
    ordnung: Bauordnung,
    vorlage: Partial<Rolle>,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Rolle wurde nicht angelegt.');
    const { error } = await supabase.from(T_ROLLE).insert({
        project_id: ordnung.project_id,
        ordnung_id: ordnung.id,
        rolle: vorlage.rolle ?? '',
        funktion: vorlage.funktion ?? '',
        firma: vorlage.firma ?? '',
        name: vorlage.name ?? '',
        erreichbarkeit: vorlage.erreichbarkeit ?? '',
        // Vorgabewert 'keine': wer einer Rolle eine Weisungsbefugnis
        // zuschreiben will, muss das aktiv tun.
        weisungsbefugnis: vorlage.weisungsbefugnis ?? 'keine',
        befugnis_umfang: vorlage.befugnis_umfang ?? '',
        sortierung: vorlage.sortierung ?? 0,
    });
    if (error) throw error;
};

export const updateRolle = async (id: string, patch: Partial<Rolle>, ctx: SchreibKontext): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Rolle wurde nicht gespeichert.');
    const { error } = await supabase.from(T_ROLLE).update(patch).eq('id', id);
    if (error) throw error;
};

export const deleteRolle = async (id: string, ctx: SchreibKontext): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Rolle wurde nicht gelöscht.');
    const { error } = await supabase.from(T_ROLLE).delete().eq('id', id);
    if (error) throw error;
};

// ═══════════════════════════════════════════════════════════════════════════
// OFFENE PUNKTE
// ═══════════════════════════════════════════════════════════════════════════

export const addLuecke = async (
    ordnung: Bauordnung,
    nr: number,
    vorlage: Partial<Luecke>,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeAenderbar(ctx, 'Der offene Punkt wurde nicht angelegt.');
    const { error } = await supabase.from(T_LUECKE).insert({
        project_id: ordnung.project_id,
        ordnung_id: ordnung.id,
        nr,
        kapitel: vorlage.kapitel ?? '',
        punkt: vorlage.punkt ?? '',
        verantwortlich: vorlage.verantwortlich ?? '',
        frist: vorlage.frist ?? null,
        pflicht: vorlage.pflicht !== false,
        herkunft: vorlage.herkunft ?? 'manuell',
        sortierung: nr,
    });
    if (error) throw error;
};

export const updateLuecke = async (id: string, patch: Partial<Luecke>, ctx: SchreibKontext): Promise<void> => {
    pruefeAenderbar(ctx, 'Der offene Punkt wurde nicht gespeichert.');
    const { error } = await supabase.from(T_LUECKE).update(patch).eq('id', id);
    if (error) throw error;
};

export const deleteLuecke = async (id: string, ctx: SchreibKontext): Promise<void> => {
    pruefeAenderbar(ctx, 'Der offene Punkt wurde nicht gelöscht.');
    const { error } = await supabase.from(T_LUECKE).delete().eq('id', id);
    if (error) throw error;
};

// ═══════════════════════════════════════════════════════════════════════════
// AUTOMATIK — der Entwurf baut sich selbst (Plan Stufe B1)
// ═══════════════════════════════════════════════════════════════════════════
//
// Was geschrieben wird, rechnet `bausteine/automatik.ts` (rein). Hier stehen
// nur die Schreibwege — mit denselben vier Hausregeln: Schreibkontext Pflicht,
// nur Entwuerfe (Bedingung IM UPDATE), nichts beim blossen Lesen.

const NICHT_MEHR_ENTWURF = 'Die Baustellenordnung ist inzwischen kein Entwurf mehr. Vermutlich wurde sie in einer '
    + 'anderen Sitzung freigegeben. Bitte neu laden.';

/** `pruefeAenderbar` plus der Status, den der Kopfsatz selbst traegt. */
const pruefeEntwurf = (ordnung: Bauordnung, ctx: SchreibKontext, was: string): void => {
    pruefeAenderbar(ctx, was);
    if (ordnung.status !== 'entwurf') throw new Error(GESPERRT_TEXT);
};

/** Eine Katalogregel, wie die Automatik sie schreibt — bestaetigt (Plan E2). */
const katalogZeile = (ordnung: Bauordnung, r: NeueRegel, jetzt: string) => ({
    project_id: ordnung.project_id,
    ordnung_id: ordnung.id,
    kapitel: r.kapitel,
    baustein_id: r.baustein_id,
    katalog_version: KATALOG_VERSION,
    wortlaut: r.wortlaut,
    art: r.art,
    quelle: 'katalog',
    zielgruppe: [],
    rhythmus: '',
    verantwortlich: '',
    sige_bezug: '',
    sortierung: r.sortierung,
    bestaetigt_am: jetzt,
    bestaetigt_von_name: AUTOMATIK_KATALOG_NAME,
});

const lueckenZeile = (
    ordnung: Bauordnung,
    nr: number,
    l: { kapitel: string; punkt: string; verweis: string },
    regelId: string | null,
) => ({
    project_id: ordnung.project_id,
    ordnung_id: ordnung.id,
    nr,
    kapitel: l.kapitel,
    regel_id: regelId,
    punkt: l.punkt,
    verweis: l.verweis,
    verantwortlich: '',
    frist: null,
    pflicht: true,
    herkunft: 'automatisch',
    sortierung: nr,
});

/** EIN Insert aller Katalogregeln; zurueck die Zuordnung `baustein_id` → neue Id (fuer `regel_id` der Luecken). */
const fuegeKatalogregelnEin = async (
    ordnung: Bauordnung,
    regeln: NeueRegel[],
    jetzt: string,
): Promise<Map<string, string>> => {
    const ids = new Map<string, string>();
    if (!regeln.length) return ids;
    const { data, error } = await supabase.from(T_REGEL)
        .insert(regeln.map(r => katalogZeile(ordnung, r, jetzt)))
        .select('id, baustein_id');
    if (error) throw error;
    for (const z of (data || []) as { id?: unknown; baustein_id?: unknown }[]) {
        if (z.baustein_id && z.id) ids.set(String(z.baustein_id), String(z.id));
    }
    return ids;
};

/** So oft wird ein Historie-Eintrag nach einem verlorenen Rennen neu versucht. */
const HISTORIE_VERSUCHE = 4;

const SCHREIBKONFLIKT = 'Die Baustellenordnung wurde gerade in einer anderen Sitzung geändert. '
    + 'Bitte neu laden und den Schritt wiederholen.';

/**
 * Haengt Eintraege an die Historie des Kopfsatzes — nur im Entwurf,
 * Bedingung IM UPDATE.
 *
 * Die bisherige Historie wird unmittelbar davor FRISCH gelesen, nicht vom
 * mitgegebenen Kopfsatz genommen: der Dialog schreibt in einem Zug Automatik-
 * Marker, Handbuch-Eintrag, KI-Marker und Abwahlen. Ein Eintrag auf dem
 * Stand, den die Ansicht vor zwei Schreibschritten kannte, ueberschriebe die
 * dazwischen geschriebenen — ein verlorener Marker liesse Automatik oder KI
 * beim naechsten Oeffnen noch einmal laufen.
 *
 * ═══ ZWEI SITZUNGEN, EIN KOPFSATZ ═════════════════════════════════════════
 * Lesen und Zurueckschreiben sind zwei Aufrufe; dazwischen kann eine zweite
 * Sitzung ihren Marker schreiben, und das ganze `historie`-Feld wuerde ihn
 * ueberschreiben (Codex-Review 02.11.2026). Deshalb traegt das Update die
 * Bedingung `updated_at = <gelesen>` — der Trigger setzt `updated_at` bei
 * JEDEM Update der Zeile, also auch bei einem fremden Marker. Trifft das
 * Update keine Zeile, wird neu gelesen und erneut versucht; erst wenn der
 * Status nicht mehr `entwurf` ist, ist es ein Fehler. Kein `select for
 * update`, kein SQL: die Bedingung IM UPDATE ist die Hausregel (c).
 */
const ergaenzeKopfHistorie = async (
    ordnung: Bauordnung,
    eintraege: string[],
    ctx: SchreibKontext,
): Promise<void> => {
    if (!eintraege.length) return;
    for (let versuch = 1; ; versuch++) {
        const { data: zeile, error: lesefehler } = await supabase.from(T_KOPF)
            .select('historie, status, updated_at').eq('id', ordnung.id).maybeSingle();
        if (lesefehler) throw lesefehler;
        const gelesen = zeile as { historie?: unknown; status?: unknown; updated_at?: unknown } | null;
        if (gelesen && gelesen.status !== undefined && gelesen.status !== 'entwurf') throw new Error(NICHT_MEHR_ENTWURF);
        const bisher = Array.isArray(gelesen?.historie) ? (gelesen.historie as HistorienEintrag[]) : [];
        const stand = typeof gelesen?.updated_at === 'string' ? gelesen.updated_at : null;
        const jetzt = new Date().toISOString();
        const historie = eintraege.reduce((h, was) => ergaenzeHistorie(h, ctx.handelnder || '', was, jetzt), bisher);
        let update = supabase.from(T_KOPF).update({ historie })
            .eq('id', ordnung.id)
            .eq('status', 'entwurf');
        // Ohne gelesenen Zeitstempel (Altbestand, Testkette) gibt es keine
        // Rennbedingung zu pruefen — dann wie bisher nur die Entwurfsbedingung.
        if (stand) update = update.eq('updated_at', stand);
        const { data, error } = await update.select('id');
        if (error) throw error;
        if (data?.length) return;
        // Ohne Waechter kann nur die Entwurfsbedingung gegriffen haben. Mit
        // Waechter und erschoepften Versuchen war es ein Rennen, kein
        // Statuswechsel — der Satz sagt das (Opus-Review 02.11.2026).
        if (!stand) throw new Error(NICHT_MEHR_ENTWURF);
        if (versuch >= HISTORIE_VERSUCHE) throw new Error(SCHREIBKONFLIKT);
        // Keine Zeile getroffen, Status war Entwurf: jemand hat dazwischen
        // geschrieben — neu lesen, auf dessen Stand anhaengen.
    }
};

/**
 * Schreibt das Paket des Automatik-Laufs — in dieser Reihenfolge:
 *
 *  1. Kopfsatz (nur leere Felder, `eq('status','entwurf')` IM UPDATE);
 *  2. Handbuch-Uebernahme ueber den vorhandenen Weg, Zeilen BESTAETIGT (E7)
 *     — der Riegel `uebernommen_handbuch_am` steht dort;
 *  3. EIN Insert aller Katalogregeln (bestaetigt, `Automatik (Katalog)`);
 *  4. EIN Insert der Rollen;
 *  5. EIN Insert der Luecken, Nummern fortlaufend ab `ersteLueckenNr`,
 *     `regel_id` ueber die eben eingefuegten Regeln;
 *  6. Erledigungen (Platzhalter steht nicht mehr im Text);
 *  7. ZULETZT der Historie-Eintrag mit dem Marker.
 *
 * Scheitert ein Schritt, fehlt der Marker, und das naechste Oeffnen holt den
 * Lauf nach — die Doppelerkennung im Paket macht das gefahrlos. Nichts bei
 * Nur-Ansicht oder freigegebener Fassung.
 *
 * Steht der Marker schon (Nachzug, §3.1 c) und ist das Paket leer, wird
 * nichts geschrieben; sonst traegt die Historie „Automatik nachgezogen".
 */
export const fuehreAutomatikAus = async (
    ordnung: Bauordnung,
    paket: AutomatikPaket,
    ctx: SchreibKontext,
): Promise<{ regeln: number; rollen: number; luecken: number }> => {
    pruefeEntwurf(ordnung, ctx, 'Der Entwurf wurde nicht automatisch ergänzt.');
    const markerFehlt = automatikFehlt(ordnung);
    if (paket.leer && !markerFehlt) return { regeln: 0, rollen: 0, luecken: 0 };

    const jetzt = new Date().toISOString();

    // ─── 1. Kopfsatz ─────────────────────────────────────────────────────
    // `historie` schreibt nur Schritt 7 — ein Patch, der sie mitbraechte,
    // ueberschriebe den Eintrag der Handbuch-Uebernahme.
    const { historie: _ohneHistorie, ...kopfPatch } = paket.kopfPatch;
    if (Object.keys(kopfPatch).length) {
        const { data, error } = await supabase.from(T_KOPF).update(kopfPatch)
            .eq('id', ordnung.id)
            .eq('status', 'entwurf')
            .select('id');
        if (error) throw error;
        if (!data?.length) throw new Error(NICHT_MEHR_ENTWURF);
    }

    // ─── 2. Handbuch ─────────────────────────────────────────────────────
    if (paket.handbuch) {
        await uebernehmeAusHandbuch({ ...ordnung, ...kopfPatch }, paket.handbuch, ctx, { bestaetigt: true });
    }

    // ─── 3. Regeln ───────────────────────────────────────────────────────
    const regelIds = await fuegeKatalogregelnEin(ordnung, paket.regeln, jetzt);

    // ─── 4. Rollen ───────────────────────────────────────────────────────
    if (paket.rollen.length) {
        const { error } = await supabase.from(T_ROLLE).insert(paket.rollen.map(r => ({
            project_id: ordnung.project_id,
            ordnung_id: ordnung.id,
            ...r,
        })));
        if (error) throw error;
    }

    // ─── 5. Luecken ──────────────────────────────────────────────────────
    if (paket.luecken.length) {
        const { error } = await supabase.from(T_LUECKE).insert(paket.luecken.map((l, i) => lueckenZeile(
            ordnung,
            paket.ersteLueckenNr + i,
            l,
            l.regelId ?? (l.bausteinId ? regelIds.get(l.bausteinId) ?? null : null),
        )));
        if (error) throw error;
    }

    // ─── 6. Erledigungen ─────────────────────────────────────────────────
    if (paket.erledigen.length) {
        const { error } = await supabase.from(T_LUECKE).update({ erledigt_am: heute() })
            .in('id', paket.erledigen)
            .is('erledigt_am', null);
        if (error) throw error;
    }

    // ─── 7. Marker — zuletzt, auf die frisch gelesene Historie ───────────
    await ergaenzeKopfHistorie(ordnung, [paket.historie], ctx);

    return { regeln: paket.regeln.length, rollen: paket.rollen.length, luecken: paket.luecken.length };
};

/**
 * „Stimmt so" zu mehreren Regeln (KI-Ergaenzungen, Plan B3): EIN Update.
 * Schon bestaetigte Zeilen behalten Datum und Namen ihrer Bestaetigung.
 */
export const bestaetigeRegeln = async (ids: string[], ctx: SchreibKontext): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Bestätigung wurde nicht gespeichert.');
    if (!ids.length) return;
    const { error } = await supabase.from(T_REGEL).update({
        bestaetigt_am: new Date().toISOString(),
        bestaetigt_von_name: ctx.handelnder || '',
    })
        .in('id', ids)
        .is('bestaetigt_am', null);
    if (error) throw error;
};

/** „Stimmt nicht" zu mehreren Regeln: EIN Delete. Fuer Katalogregeln `loescheRegelMitAbwahl`. */
export const loescheRegeln = async (ids: string[], ctx: SchreibKontext): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Regelungen wurden nicht gelöscht.');
    if (!ids.length) return;
    const { error } = await supabase.from(T_REGEL).delete().in('id', ids);
    if (error) throw error;
};

/**
 * Loescht eine Regel und vermerkt bei einem Katalogbaustein die Abwahl
 * (`abgewaehlt:<id>`, Plan F15) — sonst legte der naechste Lauf sie wieder
 * an, und die Expertenansicht waere wirkungslos.
 *
 * Der Vermerk geht ZUERST: scheitert danach das Loeschen, steht die Regel
 * noch da und der Vermerk schadet nicht. Umgekehrt kaeme sie zurueck.
 */
export const loescheRegelMitAbwahl = async (
    regel: Regel,
    ordnung: Bauordnung,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeEntwurf(ordnung, ctx, 'Die Regelung wurde nicht gelöscht.');
    if (regel.baustein_id) {
        await ergaenzeKopfHistorie(ordnung, [abgewaehltMarker(regel.baustein_id)], ctx);
    }
    const { error } = await supabase.from(T_REGEL).delete().eq('id', regel.id);
    if (error) throw error;
};

const LEERE_ANTWORT = 'Bitte eine Antwort eintragen. Ohne sie bleibt der Platzhalter im Text stehen.';
const WORTLAUT_GEAENDERT = 'Die Regelung wurde inzwischen geändert. Bitte neu laden und den Punkt erneut beantworten.';

/**
 * Beantwortet alle offenen Punkte DERSELBEN Frage (gleicher `verweis`) in
 * einem Zug: je Regel wird der Platzhalter (`[Kurz]`, `{name}`) durch die
 * Antwort ersetzt, danach werden die Punkte erledigt.
 *
 * Der Wortlaut wird nur ersetzt, wenn er noch so lautet, wie er gelesen
 * wurde (`eq('wortlaut', …)` IM UPDATE) — eine Aenderung aus einer zweiten
 * Sitzung ginge sonst still verloren. Eine Luecke ohne Platzhalter-Verweis
 * oder ohne mitgegebene Regel wird nur erledigt.
 */
export const beantworteLuecken = async (
    luecken: Luecke[],
    regeln: Regel[],
    antwort: string,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeAenderbar(ctx, 'Die Antwort wurde nicht gespeichert.');
    const wert = String(antwort || '').trim();
    if (!wert) throw new Error(LEERE_ANTWORT);
    if (!luecken.length) return;
    if (new Set(luecken.map(l => l.verweis)).size > 1) {
        throw new Error('Es lassen sich nur offene Punkte derselben Frage gemeinsam beantworten.');
    }

    const nachId = new Map(regeln.map(r => [r.id, r]));
    for (const l of luecken) {
        const name = platzhalterAusVerweis(l.verweis);
        const r = l.regel_id ? nachId.get(l.regel_id) : undefined;
        if (!name || !r) continue;
        const neu = ersetzePlatzhalter(r.wortlaut, name, wert);
        if (neu === r.wortlaut) continue;
        const { data, error } = await supabase.from(T_REGEL).update({ wortlaut: neu })
            .eq('id', r.id)
            .eq('wortlaut', r.wortlaut)
            .select('id');
        if (error) throw error;
        if (!data?.length) throw new Error(WORTLAUT_GEAENDERT);
        nachId.set(r.id, { ...r, wortlaut: neu });
    }

    const offen = luecken.filter(l => !l.erledigt_am).map(l => l.id);
    if (!offen.length) return;
    const { error } = await supabase.from(T_LUECKE).update({ erledigt_am: heute() })
        .in('id', offen)
        .is('erledigt_am', null);
    if (error) throw error;
};

/** Ein offener Punkt, eine Antwort — `beantworteLuecken` fuer genau einen. */
export const beantworteLuecke = async (
    luecke: Luecke,
    regel: Regel | null,
    antwort: string,
    ctx: SchreibKontext,
): Promise<void> => beantworteLuecken([luecke], regel ? [regel] : [], antwort, ctx);

/** Naechste freie Luecken-Nummer, frisch gelesen — der Unique-Index `(ordnung_id, nr)` ist der Riegel. */
const naechsteNummerAusDb = async (ordnungId: string): Promise<number> => {
    const { data, error } = await supabase.from(T_LUECKE).select('nr').eq('ordnung_id', ordnungId);
    if (error) throw error;
    return ((data || []) as { nr?: unknown }[]).reduce((max, z) => Math.max(max, Number(z.nr) || 0), 0) + 1;
};

/**
 * Antwort auf „Merkmal klären" (Plan E2).
 *
 *   ja    die Regeln (aus `klaerungsRegeln(…, true)`) werden eingefuegt wie
 *         im Automatik-Lauf — bestaetigt —, ihre offenen Platzhalter werden
 *         offene Punkte;
 *   nein  je Baustein der Abwahl-Vermerk (aus `klaerungsRegeln(…, false)`),
 *         damit der naechste Lauf ihn nicht wieder fragt.
 *
 * In beiden Faellen ist der Punkt danach erledigt. Das Merkmal der
 * Planungsphase bleibt unberuehrt — es gehoert der Planungsphase.
 */
export const klaerungBeantworten = async (
    ordnung: Bauordnung,
    luecke: Luecke,
    ja: boolean,
    regeln: NeueRegel[],
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeEntwurf(ordnung, ctx, 'Die Antwort wurde nicht gespeichert.');

    if (ja) {
        const regelIds = await fuegeKatalogregelnEin(ordnung, regeln, new Date().toISOString());
        const offene = regeln.flatMap(r => r.offenePlatzhalter.map(name => ({ r, name })));
        if (offene.length) {
            const erste = await naechsteNummerAusDb(ordnung.id);
            const { error } = await supabase.from(T_LUECKE).insert(offene.map(({ r, name }, i) => lueckenZeile(
                ordnung,
                erste + i,
                { kapitel: r.kapitel, punkt: platzhalterDefinition(name).frage, verweis: platzhalterVerweis(name) },
                regelIds.get(r.baustein_id) ?? null,
            )));
            if (error) throw error;
        }
    } else {
        const ids = Array.from(new Set(regeln.map(r => r.baustein_id).filter(Boolean)));
        await ergaenzeKopfHistorie(ordnung, ids.map(abgewaehltMarker), ctx);
    }

    const { error } = await supabase.from(T_LUECKE).update({ erledigt_am: heute() })
        .eq('id', luecke.id)
        .is('erledigt_am', null);
    if (error) throw error;
};

/** Ein Historie-Eintrag (KI-Marker u. a.) — nur im Entwurf. */
export const schreibeHistorie = async (
    ordnung: Bauordnung,
    was: string,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeEntwurf(ordnung, ctx, 'Der Vermerk wurde nicht gespeichert.');
    await ergaenzeKopfHistorie(ordnung, [was], ctx);
};

/**
 * „Weglassen" mehrerer Bausteine (Karte „Neu dazu", Plan §3.2): EIN
 * Historie-Update mit je einem Abwahl-Vermerk — der naechste Lauf legt sie
 * nicht wieder an (F15). Ohne Regel in der Datenbank, deshalb ohne Loeschen.
 */
export const waehleBausteineAb = async (
    ordnung: Bauordnung,
    bausteinIds: string[],
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeEntwurf(ordnung, ctx, 'Die Abwahl wurde nicht gespeichert.');
    const ids = Array.from(new Set(bausteinIds.filter(Boolean)));
    await ergaenzeKopfHistorie(ordnung, ids.map(abgewaehltMarker), ctx);
};

/**
 * Zwei Sitzungen, zwei Automatik-Laeufe (Plan F17): die Doppelerkennung
 * greift im Client, nicht in der Datenbank — ein Eindeutigkeitsindex waere
 * SQL. Nach dem Lauf neu laden und hiermit die juengere Zeile je (Kapitel,
 * Baustein) still loeschen. Zurueck kommt die Zahl der geloeschten Zeilen.
 */
export const bereinigeDoppelte = async (bundle: BauordnungBundle, ctx: SchreibKontext): Promise<number> => {
    pruefeAenderbar(ctx, 'Doppelte Regelungen wurden nicht bereinigt.');
    const o = bundle.ordnung;
    if (!o || o.status !== 'entwurf') return 0;
    const regelIds = doppelteRegeln(bundle.regeln);
    if (regelIds.length) {
        const { error } = await supabase.from(T_REGEL).delete().in('id', regelIds);
        if (error) throw error;
    }
    // Rollen genauso (Codex-Review 02.11.2026): zwei Automatik-Laeufe legen
    // beide „SiGeKo Ausführungsphase" an, und die Organisation stuende doppelt
    // im Dokument.
    const rollenIds = doppelteRollen(bundle.rollen);
    if (rollenIds.length) {
        const { error } = await supabase.from(T_ROLLE).delete().in('id', rollenIds);
        if (error) throw error;
    }
    return regelIds.length + rollenIds.length;
};

// ═══════════════════════════════════════════════════════════════════════════
// EIGENE KAPITEL — firmenweit
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Legt ein Kapitel an, das in JEDEM Dokument dieser Firma zur Verfuegung
 * steht. Beide Mandantenspalten werden mitgeschrieben, damit die Zeile den
 * Backfill in jede Richtung uebersteht (Begruendung in bauordnung.sql, 0c).
 */
export const addEigenesKapitel = async (
    zeile: Omit<KapitelZeile, 'id' | 'company_id' | 'company_name'>,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeSchreibrecht(ctx, 'Das Kapitel wurde nicht angelegt.');
    const firma = await meineFirma();
    if (!firma.company_id && !firma.company_name.trim()) {
        throw new Error(
            'Im Profil ist keine Firma hinterlegt. Ohne sie lässt sich ein firmenweites Kapitel '
            + 'nicht zuordnen. Bitte zuerst in den Einstellungen die Firma eintragen.',
        );
    }
    const { error } = await supabase.from(T_KAPITEL).insert({
        company_id: firma.company_id,
        company_name: firma.company_name,
        kapitel_id: zeile.kapitel_id,
        titel: zeile.titel,
        dokument: zeile.dokument,
        teil: zeile.teil,
        nach: zeile.nach,
        pflicht: zeile.pflicht,
        waehlbar: zeile.waehlbar,
        quelle: zeile.quelle.length ? zeile.quelle : ['erfasst'],
        wenn: zeile.wenn,
        nicht_wenn: zeile.nicht_wenn,
        angelegt_von_name: ctx.handelnder || '',
    });
    if (error) throw error;
};

export const updateEigenesKapitel = async (
    id: string,
    patch: Partial<KapitelZeile>,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeSchreibrecht(ctx, 'Das Kapitel wurde nicht gespeichert.');
    // `kapitel_id` wird bewusst NICHT durchgereicht: an ihr haengen die
    // erfassten Regelungen. Eine mitwandernde Kennung leerte das Kapitel.
    const { kapitel_id: _ignoriert, id: _id, company_id: _cid, company_name: _cn, ...erlaubt } = patch;
    const { error } = await supabase.from(T_KAPITEL).update(erlaubt).eq('id', id);
    if (error) throw error;
};

export const deleteEigenesKapitel = async (id: string, ctx: SchreibKontext): Promise<void> => {
    pruefeSchreibrecht(ctx, 'Das Kapitel wurde nicht gelöscht.');
    const { error } = await supabase.from(T_KAPITEL).delete().eq('id', id);
    if (error) throw error;
};

// ═══════════════════════════════════════════════════════════════════════════
// FREIGABE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Friert den Stand ein und setzt den Status.
 *
 * Die Reihenfolge ist verbindlich: ZUERST wird eingefroren, DANN der Status
 * gesetzt — beides in EINEM Update. Ein Statuswechsel ohne eingefrorene
 * Kapitelliste ergaebe ein unterschriebenes Dokument, das beim naechsten
 * Laden anders aussieht.
 *
 * Zurueck kommt der freigegebene Kopfsatz, wie ihn die Datenbank jetzt
 * fuehrt. Die Ablage der Fassung (`bauordnung/ablage/bauordnungAblage.ts`)
 * baut ihre PDFs aus GENAU diesem Stand — ein PDF aus dem Entwurfs-Buendel
 * truege noch den Entwurfsvermerk.
 *
 * Die Dokument-Ids der VORIGEN Fassung werden dabei geleert: die neue
 * Fassung hat noch kein abgelegtes Dokument, und ein stehengebliebener
 * Verweis liesse die Ansicht „abgelegt" sagen, waehrend die Ablage der neuen
 * Fassung gescheitert ist.
 */
export const freigebenBauordnung = async (
    bundle: BauordnungBundle,
    merkmale: ProjektMerkmale,
    versionLabel: string,
    ctx: SchreibKontext,
): Promise<Bauordnung> => {
    pruefeAenderbar(ctx, 'Die Freigabe wurde nicht gespeichert.');
    const o = bundle.ordnung;
    if (!o) throw new Error('Es gibt keine Baustellenordnung, die freigegeben werden könnte.');

    const eigene = bundle.eigeneKapitel.map(alsEigenesKapitel);
    const kennungen = (d: Dokumentart) =>
        gezogeneKapitel(d, merkmale, d === 'hse' ? o.hse_zusatzkapitel : [], eigene)
            .map(g => g.definition.id);

    const eingefroren: Partial<Record<Dokumentart, string[]>> = {};
    if (o.erzeugt_ordnung) eingefroren.ordnung = kennungen('ordnung');
    if (o.erzeugt_hse) eingefroren.hse = kennungen('hse');

    // Ein leeres Fassungsfeld loescht die Fassung NICHT: die Automatik setzt
    // „Rev. 00", und die Regeltexte drucken `{revision_nr}` aus genau diesem
    // Feld (`loeseKopfPlatzhalter`) — ein `null` stuende im freigegebenen
    // Dokument als „Revision [Fassung]". Leer heisst „so lassen".
    const fassung = versionLabel.trim() || o.version_label || null;
    // Die Historie FRISCH lesen: der mitgegebene Kopfsatz kann Marker einer
    // zweiten Sitzung (Abwahl, KI) noch nicht kennen, und die Freigabe
    // schriebe das ganze Feld aus ihrem Stand zurueck — eine abgewaehlte
    // Katalogregel kaeme in der naechsten Fassung wieder (F15). Mit dem
    // gelesenen `updated_at` als Waechter IM UPDATE: hat sich der Kopfsatz
    // seit dem Lesen geaendert, greift die Freigabe nicht, und der Mensch
    // laedt neu (Opus-Review 02.11.2026).
    const { data: frisch, error: lesefehler } = await supabase.from(T_KOPF)
        .select('historie, status, updated_at').eq('id', o.id).maybeSingle();
    if (lesefehler) throw lesefehler;
    const gelesen = frisch as { historie?: unknown; status?: unknown; updated_at?: unknown } | null;
    if (gelesen && gelesen.status !== undefined && gelesen.status !== 'entwurf') throw new Error(NICHT_MEHR_ENTWURF);
    const historieFrisch = Array.isArray(gelesen?.historie) ? (gelesen.historie as HistorienEintrag[]) : o.historie;
    const stand = typeof gelesen?.updated_at === 'string' ? gelesen.updated_at : null;

    const jetzt = new Date().toISOString();
    let update = supabase.from(T_KOPF).update({
        eingefroren_kapitel: eingefroren,
        eingefroren_merkmale: merkmale as Record<string, unknown>,
        status: 'freigegeben',
        version_label: fassung,
        freigegeben_am: jetzt,
        freigegeben_von_name: ctx.handelnder || '',
        dokument_id_ordnung: null,
        dokument_id_hse: null,
        historie: ergaenzeHistorie(historieFrisch, ctx.handelnder || '', `Freigegeben (${fassung || 'ohne Fassungsbezeichnung'})`, jetzt),
    })
        .eq('id', o.id)
        // Nur ein Entwurf kann freigegeben werden — die Bedingung steht IM
        // UPDATE, damit zwei gleichzeitige Freigaben nicht beide durchgehen.
        .eq('status', 'entwurf');
    if (stand) update = update.eq('updated_at', stand);
    const { data, error } = await update
        .select('*')
        .maybeSingle();
    if (error) throw error;
    // Keine Zeile getroffen heisst: eine Bedingung hat gegriffen — eine zweite
    // Sitzung war schneller (freigegeben oder geaendert). Kein stiller Erfolg.
    if (!data) {
        throw new Error('Die Freigabe hat nichts geändert. Die Fassung wurde inzwischen in einer anderen '
            + 'Sitzung geändert oder freigegeben. Bitte neu laden und die Freigabe erneut auslösen.');
    }
    return alsKopf(data as Record<string, unknown>);
};

/**
 * Vermerkt die abgelegten Dokumente einer freigegebenen Fassung am Kopfsatz
 * (`dokument_id_ordnung` / `dokument_id_hse`, bauordnung.sql).
 *
 * Die Bedingungen stehen IM UPDATE: nur die freigegebene Fassung MIT
 * demselben Freigabezeitpunkt bekommt die Ids. Zwischen Ablage und Vermerk
 * kann eine zweite Sitzung eine neue Fassung eroeffnen und wieder freigeben —
 * dann gehoeren die Ids zu einer alten Fassung und duerfen nicht an der neuen
 * stehen. Trifft das Update keine Zeile, wird das gemeldet: die Dokumente
 * liegen in der Ablage (`fassung_ref` benennt die Fassung), nur der Verweis
 * am Kopfsatz fehlt.
 *
 * Nur `pruefeSchreibrecht`, nicht `pruefeAenderbar`: geschrieben wird
 * absichtlich an einer FREIGEGEBENEN Fassung, so wie bei `revisionEroeffnen`.
 */
export const verknuepfeFreigabeDokumente = async (
    ordnung: Pick<Bauordnung, 'id' | 'freigegeben_am'>,
    ids: Partial<Record<Dokumentart, string>>,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeSchreibrecht(ctx, 'Die abgelegten Dokumente wurden nicht am Kopfsatz vermerkt.');
    const patch: { dokument_id_ordnung?: string; dokument_id_hse?: string } = {};
    if (ids.ordnung) patch.dokument_id_ordnung = ids.ordnung;
    if (ids.hse) patch.dokument_id_hse = ids.hse;
    if (!Object.keys(patch).length) return;
    if (!ordnung.freigegeben_am) {
        throw new Error('Ohne Freigabezeitpunkt lässt sich die Fassung nicht sicher wiederfinden.');
    }

    const { data, error } = await supabase.from(T_KOPF).update(patch)
        .eq('id', ordnung.id)
        .eq('status', 'freigegeben')
        .eq('freigegeben_am', ordnung.freigegeben_am)
        .select('id');
    if (error) throw error;
    if (!data?.length) {
        throw new Error('Die Fassung ist inzwischen nicht mehr die freigegebene. Die Dokumente liegen in der '
            + 'Projektablage, sind aber nicht am Kopfsatz vermerkt.');
    }
};

/** Eroeffnet eine neue Fassung. Die freigegebene bleibt in der Historie. */
export const revisionEroeffnen = async (
    ordnung: Bauordnung,
    ctx: SchreibKontext,
): Promise<void> => {
    pruefeSchreibrecht(ctx, 'Die Revision wurde nicht eröffnet.');
    const jetzt = new Date().toISOString();
    const { error } = await supabase.from(T_KOPF).update({
        status: 'entwurf',
        freigegeben_am: null,
        freigegeben_von_name: '',
        eingefroren_kapitel: null,
        // Der Text ist zugleich der Fassungsindex der Marker (`fassungsIndex`,
        // Plan F13) — deshalb die Konstante, kein zweites Literal.
        historie: ergaenzeHistorie(ordnung.historie, ctx.handelnder || '', NEUE_FASSUNG_TEXT, jetzt),
    })
        .eq('id', ordnung.id)
        .eq('status', 'freigegeben');
    if (error) throw error;
};

// ═══════════════════════════════════════════════════════════════════════════
// ABRECHNUNG (Spezifikation J.4)
// ═══════════════════════════════════════════════════════════════════════════

/** Das gebuchte Dokument — Baustellenordnung oder HSE-Plan. */
export type BoDokument = Dokumentart;

/**
 * Vermerkt die Buchung EINES Dokuments auf einer LV-Position — genau einmal.
 *
 * Rueckgabe `true` = dieser Aufruf hat gebucht. `false` = es wurde NICHTS
 * geschrieben: das Dokument war schon gebucht oder die Fassung ist nicht
 * (mehr) freigegeben. Kein Fehler — der zweite Klick ist der Normalfall.
 *
 * Der Riegel ist `.is(<gebucht_am des Dokuments>, null)` IN der WHERE-Klausel
 * (Vorbild `sigeEinweisungService.markiereGebucht`), nicht ein vorgelagertes
 * SELECT: bei zwei gleichzeitigen Klicks gewaenne sonst, wer zuletzt
 * schreibt, und die Leistung stuende zweimal in der Abrechnung. Zwei
 * Spaltenpaare, weil es zwei Dokumente sind — ein einzelnes `gebucht_am`
 * sperrte nach der Ordnung den HSE-Plan fuer immer (B.1).
 *
 * `eq('status','freigegeben')` steht ebenfalls im UPDATE: gebucht wird die
 * erbrachte Leistung, und erbracht ist sie mit der Freigabe (J.2). Nur
 * `pruefeSchreibrecht`, nicht `pruefeAenderbar` — geschrieben wird absichtlich
 * an einer FREIGEGEBENEN Fassung.
 */
export const markiereGebucht = async (
    ordnungId: string,
    dokument: BoDokument,
    positionId: string,
    menge: number,
    ctx: SchreibKontext,
    /**
     * Der Zeitstempel, der geschrieben wird — vom Aufrufer gebildet, damit er
     * GENAU diesen Vermerk spaeter bedingt zuruecknehmen kann
     * (`nimmGebuchtZurueck`, Plan Paketposition B5 / Codex R2c). Ohne Angabe
     * wie bisher „jetzt".
     */
    stempel: string = new Date().toISOString(),
): Promise<boolean> => {
    pruefeSchreibrecht(ctx, 'Die Leistung wurde nicht gebucht.');
    const feld = GEBUCHT_AM_SPALTE[dokument];
    const { data, error } = await supabase.from(T_KOPF).update({
        [feld]: stempel,
        [GEBUCHTE_POSITION_SPALTE[dokument]]: positionId,
        [GEBUCHTE_MENGE_SPALTE[dokument]]: menge,
    })
        .eq('id', ordnungId)
        .eq('status', 'freigegeben')
        .is(feld, null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return !!data && data.length > 0;
};

/**
 * KOMPENSATION: nimmt GENAU den eigenen Vermerk von `markiereGebucht` zurueck
 * — nur, wenn er noch mit diesem Stempel dasteht (Bedingung IM UPDATE, wie
 * der Riegel selbst). Fuer den Fall, dass die Gutschrift ins Projekt nach dem
 * Riegel nicht geschrieben werden konnte (Modul verlassen, Projekt
 * gewechselt, Position fehlt): ohne Ruecknahme galte das Dokument beim
 * naechsten Oeffnen als gebucht, ein neuer Versuch liefe in den Riegel
 * (`false`), und die Leistung waere verloren (Codex R2c).
 *
 * Rueckgabe `true` = zurueckgenommen; `false` = der Vermerk trug einen
 * anderen Stempel oder war schon weg (dann gehoert er nicht diesem Vorgang).
 * Wirft bei Datenbankfehlern. Nur Schreibrecht (wie der Riegel), die RLS des
 * Kopfsatzes (Projektzugriff, Nr. 43) erlaubt das Update.
 */
export const nimmGebuchtZurueck = async (
    ordnungId: string,
    dokument: BoDokument,
    stempel: string,
    ctx: SchreibKontext,
): Promise<boolean> => {
    pruefeSchreibrecht(ctx, 'Der Buchungsvermerk wurde nicht zurückgenommen.');
    const feld = GEBUCHT_AM_SPALTE[dokument];
    const { data, error } = await supabase.from(T_KOPF).update({
        [feld]: null,
        [GEBUCHTE_POSITION_SPALTE[dokument]]: null,
        [GEBUCHTE_MENGE_SPALTE[dokument]]: null,
    })
        .eq('id', ordnungId)
        .eq(feld, stempel)
        .select('id');
    if (error) throw new Error(describeError(error));
    return !!data && data.length > 0;
};

// ═══════════════════════════════════════════════════════════════════════════
// GEFUEHRTER DIALOG (Plan Stufe B2/B3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Die Schreibwege, die nur der gefuehrte Dialog braucht. Was geschrieben wird,
// rechnet `bauordnung/dialog/dialogLogik.ts` (rein); hier gelten dieselben
// Hausregeln: Schreibkontext Pflicht, nur Entwuerfe, Bedingung IM UPDATE.

/** Eine KI-Ergaenzung, wie der Dialog sie einsortiert hat (`kiRegelZeilen`). */
export interface KiRegelVorlage {
    kapitel: string;
    wortlaut: string;
    art: Regel['art'];
    sortierung: number;
}

/** Wortlaut fuer den Doppelvergleich — dieselbe Form wie `doppelteRegeln`. */
const vergleichsWortlaut = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Die Vorschlaege der KI als Regeln — EIN Insert, `quelle: 'ki'`, OHNE
 * Bestaetigung (Spec F.5, Ebene 4): ins Dokument kommen sie erst, wenn ein
 * Mensch „Stimmt so" sagt (`bestaetigeRegeln`). Gespeichert statt nur
 * angezeigt, damit das Ergebnis nach einem Neuladen noch da ist und kein
 * zweiter Aufruf noetig wird. Danach der Marker `ki:<Fassung>` — in EINEM
 * Aufruf mit dem Insert: scheitert der Insert, fehlt er, und das naechste
 * Oeffnen fragt erneut.
 *
 * ═══ ZWEI SITZUNGEN, ZWEI KI-LAEUFE (Codex-Review 02.11.2026) ═════════════
 * Beide sehen beim Start denselben fehlenden Marker. Deshalb wird HIER, direkt
 * vor dem Insert, der Kopfsatz frisch gelesen: steht der Marker inzwischen,
 * hat die andere Sitzung gewonnen — nichts schreiben, `0` zurueck. Sonst die
 * Regeln frisch lesen und Wortgleiches aussortieren. Was in der Luecke
 * zwischen Lesen und Insert noch durchrutscht (nur noch die Laufzeit zweier
 * Datenbankaufrufe), raeumt dieselbe Funktion NACH dem Insert weg: Regeln
 * erneut lesen, wortgleiche KI-Zeilen desselben Kapitels bis auf die erste
 * loeschen (`doppelteRegeln`). Nicht erst beim naechsten Oeffnen — die
 * Uebersicht zeigte sonst beide, und „Stimmt so" bestaetigte beide.
 *
 * Zurueck kommt die Zahl der eingefuegten Zeilen — `0` auch dann, wenn alles
 * schon da war und nur der Marker geschrieben wurde.
 */
export const fuegeKiVorschlaegeEin = async (
    ordnung: Bauordnung,
    vorlagen: KiRegelVorlage[],
    ctx: SchreibKontext,
    marker: string,
): Promise<number> => {
    pruefeEntwurf(ordnung, ctx, 'Die Vorschläge der KI wurden nicht gespeichert.');

    const { data: kopf, error: kopfFehler } = await supabase.from(T_KOPF)
        .select('historie, status').eq('id', ordnung.id).maybeSingle();
    if (kopfFehler) throw kopfFehler;
    const frisch = kopf as { historie?: unknown; status?: unknown } | null;
    if (frisch && frisch.status !== undefined && frisch.status !== 'entwurf') throw new Error(NICHT_MEHR_ENTWURF);
    const historieFrisch = Array.isArray(frisch?.historie) ? (frisch.historie as HistorienEintrag[]) : ordnung.historie;
    if (!kiFehlt({ ...ordnung, historie: historieFrisch })) return 0;

    const { data: vorhandene, error: regelFehler } = await supabase.from(T_REGEL)
        .select('kapitel, wortlaut').eq('ordnung_id', ordnung.id);
    if (regelFehler) throw regelFehler;
    const schonDa = new Set<string>();
    for (const r of (Array.isArray(vorhandene) ? vorhandene : []) as { kapitel?: unknown; wortlaut?: unknown }[]) {
        if (typeof r.kapitel === 'string' && typeof r.wortlaut === 'string') {
            schonDa.add(`${r.kapitel}|${vergleichsWortlaut(r.wortlaut)}`);
        }
    }
    const zeilen: KiRegelVorlage[] = [];
    for (const v of vorlagen) {
        if (!v.kapitel || !v.wortlaut.trim()) continue;
        const k = `${v.kapitel}|${vergleichsWortlaut(v.wortlaut)}`;
        if (schonDa.has(k)) continue;
        schonDa.add(k);
        zeilen.push(v);
    }
    if (!zeilen.length) {
        await ergaenzeKopfHistorie(ordnung, [marker], ctx);
        return 0;
    }
    const { error } = await supabase.from(T_REGEL).insert(zeilen.map(v => ({
        project_id: ordnung.project_id,
        ordnung_id: ordnung.id,
        kapitel: v.kapitel,
        baustein_id: null,
        // Kein Baustein, keine Katalogfassung — der Wortlaut stammt von der KI.
        katalog_version: '',
        wortlaut: v.wortlaut.trim(),
        art: v.art === 'koordinierungshinweis' ? 'koordinierungshinweis' : 'regel',
        quelle: 'ki',
        zielgruppe: [],
        rhythmus: '',
        verantwortlich: '',
        sige_bezug: '',
        sortierung: v.sortierung,
        bestaetigt_am: null,
        bestaetigt_von_name: '',
    })));
    if (error) throw error;

    // Nachlese: hat eine zweite Sitzung im selben Augenblick dieselben Saetze
    // eingefuegt, stehen sie jetzt doppelt — die juengeren weg, still.
    const { data: danach, error: nachleseFehler } = await supabase.from(T_REGEL)
        .select('id, kapitel, wortlaut, baustein_id, quelle, sortierung').eq('ordnung_id', ordnung.id);
    if (nachleseFehler) throw nachleseFehler;
    const doppelt = doppelteRegeln((Array.isArray(danach) ? danach : []) as Regel[]);
    if (doppelt.length) {
        const { error: loeschFehler } = await supabase.from(T_REGEL).delete().in('id', doppelt);
        if (loeschFehler) throw loeschFehler;
    }

    await ergaenzeKopfHistorie(ordnung, [marker], ctx);
    return zeilen.length - doppelt.length;
};

/**
 * „Regelung weglassen" auf einer Karte des Dialogs: alle Regeln einer Frage
 * auf einmal (ein Platzhalter kann in mehreren stehen).
 *
 * Reihenfolge wie bei `loescheRegelMitAbwahl`: ZUERST die Abwahl-Vermerke
 * der Katalogbausteine (F15 — sonst legte der naechste Lauf sie wieder an),
 * DANN die offenen Punkte dieser Regeln erledigen (H.2: erledigt, nicht
 * geloescht — `regel_id` wird beim Loeschen sonst `null`, und der Punkt
 * stuende ohne Regel offen da), ZULETZT EIN Delete der Regeln.
 *
 * `weitereLuecken`: Ids offener Punkte derselben Frage, die an keiner Regel
 * (mehr) haengen — sie werden mit erledigt.
 */
export const loescheRegelnMitAbwahl = async (
    regeln: Regel[],
    ordnung: Bauordnung,
    ctx: SchreibKontext,
    weitereLuecken: string[] = [],
): Promise<void> => {
    pruefeEntwurf(ordnung, ctx, 'Die Regelungen wurden nicht gelöscht.');
    if (!regeln.length && !weitereLuecken.length) return;
    const bausteine = Array.from(new Set(regeln.map(r => r.baustein_id).filter((b): b is string => !!b)));
    if (bausteine.length) await ergaenzeKopfHistorie(ordnung, bausteine.map(abgewaehltMarker), ctx);

    const ids = regeln.map(r => r.id);
    const erledige = async (spalte: 'regel_id' | 'id', werte: string[]) => {
        if (!werte.length) return;
        const { error } = await supabase.from(T_LUECKE).update({ erledigt_am: heute() })
            .in(spalte, werte)
            .is('erledigt_am', null);
        if (error) throw error;
    };
    await erledige('regel_id', ids);
    await erledige('id', weitereLuecken);

    if (!ids.length) return;
    const { error } = await supabase.from(T_REGEL).delete().in('id', ids);
    if (error) throw error;
};
